import { useState, useMemo, useEffect, useRef } from 'react';
import { Button } from '../ui/Button';
import { AccessoriesSection } from '../ui/AccessoriesSection';
import { AffiliateInlineCard } from '../ui/AffiliateInlineCard';
import { AffiliateStickyBanner } from '../ui/AffiliateStickyBanner';
import { AffiliateTooltip } from '../ui/AffiliateTooltip';
import { SubscribeModal } from '../ui/SubscribeModal';
import { Skeleton, SkeletonNowPlaying, SkeletonPhaseGroup } from '../ui/Skeleton';
import { TreatmentCurveChart } from '../ui/TreatmentCurveChart';
import type { ResolvedTrack, GeneratedPlaylist, PlaybackState, Phase, TreatmentType, PhaseRatios, Platform } from '../../types';
import { formatDuration } from '../../lib/playlist-generator';
import { PHASE_LABELS, PHASE_DESCRIPTIONS, PHASE_BACKDROP_COLORS, getPhaseColor, PHASES_IN_ORDER, DEFAULT_DURATION_MINUTES } from '../../lib/constants';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { getAppleMusicSubscribeUrl } from '../../lib/apple-music-affiliate';
import type { AppleMusicLinks } from '../../lib/apple-music-links';
import { useAffiliateDisplay } from '../../contexts/AffiliateDisplayContext';
import { useVisualEffects } from '../../contexts/VisualEffectsContext';
import { AFFILIATE_LINKS_ENABLED } from '../../lib/affiliate-products';

const APPLE_SUBSCRIBE_UPSELL_ENABLED = import.meta.env.VITE_APPLE_SUBSCRIBE_UPSELL === 'true';

interface UnifiedPlayerScreenProps {
  playlist: GeneratedPlaylist;
  resolvedTracks: ResolvedTrack[];
  treatmentType: TreatmentType;
  isResolving: boolean;
  resolutionError: string | null;
  playbackState: PlaybackState;
  isPlaying: boolean;
  isPreviewMode: boolean;
  platform: Platform;
  currentTrackIndex: number;
  artworkUrl?: string | null;
  onPlay: () => void;
  onPause: () => void;
  onSkipNext: () => void;
  onSkipPrevious: () => void;
  onSkipToTrack: (index: number) => void;
  onSaveToLibrary: () => void;
  onNewPlaylist: () => void;
  onRegenerate: () => void;
  onRetryResolution: () => void;
  isSaving: boolean;
  hasSaved: boolean;
  appleMusicLinks: AppleMusicLinks | null;
  onConnectFromPreview?: () => void;
}

export function UnifiedPlayerScreen({
  playlist,
  resolvedTracks,
  treatmentType,
  isResolving,
  resolutionError,
  playbackState,
  isPlaying,
  isPreviewMode,
  platform,
  currentTrackIndex,
  artworkUrl,
  onPlay,
  onPause,
  onSkipNext,
  onSkipPrevious,
  onSkipToTrack,
  onSaveToLibrary,
  onNewPlaylist,
  onRegenerate,
  onRetryResolution,
  isSaving,
  hasSaved,
  appleMusicLinks,
  onConnectFromPreview,
}: UnifiedPlayerScreenProps) {
  const [showInstructions, setShowInstructions] = useState(false);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  const previewCtaLabel = onConnectFromPreview ? 'Connect for Full Tracks' : 'Upgrade to Full Tracks';
  const [hoveredMinute, setHoveredMinute] = useState<number | null>(null);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);
  const { displayMode } = useAffiliateDisplay();
  const { settings: visualEffects } = useVisualEffects();

  // Track when playback first starts (for tooltip display)
  useEffect(() => {
    if (isPlaying && !hasStartedPlaying) {
      setHasStartedPlaying(true);
    }
  }, [isPlaying, hasStartedPlaying]);
  const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useState(false);
  const [subscribeModalDismissedAt, setSubscribeModalDismissedAt] = useLocalStorage<number | null>(
    'healfm-subscribe-modal-dismissed-at',
    null
  );
  const subscribeModalDismissWindowMs = 7 * 24 * 60 * 60 * 1000;
  const isSubscribeModalDismissed =
    subscribeModalDismissedAt !== null &&
    Date.now() - subscribeModalDismissedAt < subscribeModalDismissWindowMs;
  const currentTrack = resolvedTracks[currentTrackIndex];
  const isLoading = playbackState === 'loading';
  const backdropColor = currentTrack ? PHASE_BACKDROP_COLORS[currentTrack.phase] : 'transparent';
  const primaryAppleMusicLink = appleMusicLinks?.app || appleMusicLinks?.web || '';
  const platformBadge = platform === 'youtube'
    ? { label: 'YouTube', className: 'bg-red-500/15 text-red-600 dark:text-red-300 border border-red-500/20' }
    : platform === 'preview'
      ? { label: 'Preview', className: 'bg-amber-500/15 text-amber-700 dark:text-amber-200 border border-amber-500/20' }
      : { label: 'Apple Music', className: 'bg-pink-500/15 text-pink-600 dark:text-pink-300 border border-pink-500/20' };

  const subscribeUrl = getAppleMusicSubscribeUrl('healfm_preview_banner');

  useEffect(() => {
    if (!APPLE_SUBSCRIBE_UPSELL_ENABLED) return;
    if (subscribeModalDismissedAt !== null && !isSubscribeModalDismissed) {
      setSubscribeModalDismissedAt(null);
    }
  }, [subscribeModalDismissedAt, isSubscribeModalDismissed, setSubscribeModalDismissedAt]);

  useEffect(() => {
    if (!APPLE_SUBSCRIBE_UPSELL_ENABLED) {
      setIsSubscribeModalOpen(false);
      return;
    }
    if (!isPreviewMode || isSubscribeModalDismissed || platform === 'youtube') {
      setIsSubscribeModalOpen(false);
      return;
    }
    setIsSubscribeModalOpen(true);
  }, [isPreviewMode, isSubscribeModalDismissed, platform]);

  const openSubscribeModal = () => setIsSubscribeModalOpen(true);
  const dismissSubscribeModal = () => {
    setSubscribeModalDismissedAt(Date.now());
    setIsSubscribeModalOpen(false);
  };
  const previewCtaHandler = APPLE_SUBSCRIBE_UPSELL_ENABLED
    ? (onConnectFromPreview || openSubscribeModal)
    : onConnectFromPreview;
  const showPreviewCta = Boolean(previewCtaHandler);

  // Track transition state
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [prevTrackInfo, setPrevTrackInfo] = useState<{
    title: string;
    artist: string;
    artworkUrl: string | null;
    phase: Phase;
  } | null>(null);
  const prevTrackIndexRef = useRef<number>(currentTrackIndex);
  const prevArtworkUrlRef = useRef<string | null | undefined>(artworkUrl);

  // Detect track changes and trigger transition
  useEffect(() => {
    const prevArtworkUrl = prevArtworkUrlRef.current ?? null;
    const prevTrackIndex = prevTrackIndexRef.current;

    if (currentTrackIndex !== prevTrackIndex && currentTrack) {
      // Store previous track info before transitioning
      const prevTrack = resolvedTracks[prevTrackIndex];
      if (prevTrack) {
        setPrevTrackInfo({
          title: prevTrack.title,
          artist: prevTrack.artist,
          artworkUrl: prevArtworkUrl,
          phase: prevTrack.phase,
        });
        setIsTransitioning(true);

        // Clear transition after animation completes
        const timer = setTimeout(() => {
          setIsTransitioning(false);
          setPrevTrackInfo(null);
        }, 300);

        prevTrackIndexRef.current = currentTrackIndex;
        prevArtworkUrlRef.current = artworkUrl;
        return () => clearTimeout(timer);
      }
    }
    prevTrackIndexRef.current = currentTrackIndex;
    prevArtworkUrlRef.current = artworkUrl;
  }, [currentTrackIndex, currentTrack, resolvedTracks, artworkUrl]);

  // Group resolved tracks by phase for the track list
  const tracksByPhase = PHASES_IN_ORDER.map(phase => ({
    phase,
    tracks: resolvedTracks.filter(t => t.phase === phase),
  })).filter(group => group.tracks.length > 0);

  // Calculate cumulative track indices for skipToTrack
  const getGlobalTrackIndex = (phase: Phase, localIndex: number): number => {
    let globalIndex = 0;
    for (const p of PHASES_IN_ORDER) {
      if (p === phase) {
        return globalIndex + localIndex;
      }
      globalIndex += resolvedTracks.filter(t => t.phase === p).length;
    }
    return localIndex;
  };

  // Calculate cumulative time ranges for each track
  const trackTimeRanges = useMemo(() => (
    resolvedTracks.reduce<{ index: number; startSeconds: number; endSeconds: number }[]>(
      (acc, track, index) => {
        const startSeconds = acc.length > 0 ? acc[acc.length - 1].endSeconds : 0;
        const endSeconds = startSeconds + track.durationSeconds;
        return [...acc, { index, startSeconds, endSeconds }];
      },
      []
    )
  ), [resolvedTracks]);

  const totalDurationSeconds = useMemo(() => {
    if (resolvedTracks.length > 0) {
      return resolvedTracks.reduce((sum, track) => sum + track.durationSeconds, 0);
    }
    return playlist.totalDurationSeconds;
  }, [resolvedTracks, playlist.totalDurationSeconds]);

  const durationMinutes = totalDurationSeconds > 0 ? totalDurationSeconds / 60 : DEFAULT_DURATION_MINUTES;

  const phaseRatios = useMemo<PhaseRatios | undefined>(() => {
    const sourceTracks = resolvedTracks.length > 0 ? resolvedTracks : playlist.tracks;
    if (sourceTracks.length === 0) return undefined;
    const totals = PHASES_IN_ORDER.reduce((acc, phase) => {
      acc[phase] = 0;
      return acc;
    }, {} as Record<Phase, number>);
    for (const track of sourceTracks) {
      totals[track.phase] += track.durationSeconds;
    }
    const total = Object.values(totals).reduce((sum, val) => sum + val, 0);
    if (total <= 0) return undefined;
    return {
      opening: totals.opening / total,
      building: totals.building / total,
      peak: totals.peak / total,
      longTail: totals.longTail / total,
      closing: totals.closing / total,
    };
  }, [resolvedTracks, playlist.tracks]);

  // Find which track is at the hovered time
  const highlightedTrackIndex = useMemo(() => {
    if (hoveredMinute === null) return null;
    const hoveredSeconds = hoveredMinute * 60;
    const range = trackTimeRanges.find(
      r => hoveredSeconds >= r.startSeconds && hoveredSeconds < r.endSeconds
    );
    return range?.index ?? null;
  }, [hoveredMinute, trackTimeRanges]);

  // Track which playlist item is being hovered (for chart marker)
  const [hoveredTrackIndex, setHoveredTrackIndex] = useState<number | null>(null);

  // Calculate the time position for the hovered track (midpoint of the track)
  const hoveredTrackMinute = useMemo(() => {
    if (hoveredTrackIndex === null) return null;
    const range = trackTimeRanges[hoveredTrackIndex];
    if (!range) return null;
    // Use midpoint of track
    return ((range.startSeconds + range.endSeconds) / 2) / 60;
  }, [hoveredTrackIndex, trackTimeRanges]);

  // Track info for chart tooltip (prefer list hover over chart hover)
  const tooltipTrack = useMemo(() => {
    if (hoveredTrackIndex !== null) {
      return resolvedTracks[hoveredTrackIndex] ?? null;
    }
    if (highlightedTrackIndex !== null) {
      return resolvedTracks[highlightedTrackIndex] ?? null;
    }
    return null;
  }, [hoveredTrackIndex, highlightedTrackIndex, resolvedTracks]);


  // Resolving state - show skeleton UI
  if (isResolving) {
    return (
      <div className="flex-1 flex flex-col px-6 py-4 max-w-2xl mx-auto w-full">
        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-parchment-text dark:text-white truncate">{playlist.name}</h1>
            <p className="text-sm text-parchment-400 dark:text-white/50">Loading tracks...</p>
          </div>
        </div>

        {/* Skeleton Now Playing */}
        <div className="mb-4">
          <SkeletonNowPlaying />
        </div>

        {/* Skeleton Phase Groups */}
        <div className="flex-1 overflow-y-auto space-y-4">
          {playlist.phaseBreakdown.map(({ phase, tracks }) => (
            <SkeletonPhaseGroup key={phase} trackCount={tracks.length} />
          ))}
        </div>

        {/* Disabled buttons while loading */}
        <div className="mt-4 space-y-3">
          <Button variant="secondary" size="lg" disabled className="w-full">
            <Skeleton variant="text" width={140} height={16} className="mx-auto" />
          </Button>
          <Button variant="ghost" size="lg" disabled className="w-full">
            <Skeleton variant="text" width={100} height={16} className="mx-auto" />
          </Button>
        </div>
      </div>
    );
  }

  // Resolution error state
  if (resolutionError) {
    return (
      <div className="flex-1 flex flex-col px-6 py-4 max-w-2xl mx-auto w-full items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-6">
          <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h2 className="text-xl font-medium text-parchment-text dark:text-white mb-2">Unable to Load Tracks</h2>
        <p className="text-parchment-500 dark:text-white/60 text-center mb-6">{resolutionError}</p>
        <div className="space-y-3 w-full max-w-xs">
          <Button size="lg" onClick={onRetryResolution} className="w-full">
            Try Again
          </Button>
          <Button variant="ghost" size="lg" onClick={onNewPlaylist} className="w-full">
            New Playlist
          </Button>
        </div>
      </div>
    );
  }

  // Ready/Playing state
  return (
    <div className="flex-1 flex flex-col px-6 py-4 max-w-2xl mx-auto w-full relative">
      {APPLE_SUBSCRIBE_UPSELL_ENABLED && platform !== 'youtube' && (
        <SubscribeModal
          isOpen={isSubscribeModalOpen}
          onDismiss={dismissSubscribeModal}
          subscribeUrl={subscribeUrl}
        />
      )}

      {/* Phase Backdrop */}
      <div
        className="absolute inset-0 pointer-events-none transition-[background] duration-1000"
        style={{
          background: `radial-gradient(circle at 50% 30%, ${backdropColor} 0%, transparent 70%)`,
        }}
      />

      <div className="relative z-10 flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-parchment-text dark:text-white truncate">{playlist.name}</h1>
            <div className="flex items-center gap-2 text-sm text-parchment-400 dark:text-white/50">
              <span>{resolvedTracks.length} tracks</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${platformBadge.className}`}>
                {platformBadge.label}
              </span>
            </div>
          </div>
        </div>

        {/* Preview Mode Banner */}
        {isPreviewMode && platform !== 'youtube' && (
          <div className="glass rounded-xl p-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-3 bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-center gap-3 flex-1">
              <svg className="w-5 h-5 text-amber-500 dark:text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="text-sm text-amber-700 dark:text-amber-200">
                Preview mode - 30-second samples
              </span>
            </div>
            {showPreviewCta && (
              <button
                type="button"
                onClick={previewCtaHandler}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded-lg border border-amber-500/30 text-amber-900 dark:text-amber-100 bg-amber-500/20 hover:bg-amber-500/30 hover:border-amber-500/40 transition-colors"
              >
                {previewCtaLabel}
              </button>
            )}
          </div>
        )}

        {/* Now Playing */}
        <div className="glass glass-micro rounded-2xl p-6 mb-4">
          {currentTrack ? (
            <>
              {/* Album Art with Crossfade */}
              <div className="relative w-32 h-32 mx-auto mb-4">
                {/* Ambient glow from artwork - wrapper handles play/pause fade */}
                {visualEffects.glowEffects && (
                  <div
                    className="absolute inset-0 transition-opacity duration-700"
                    style={{
                      filter: 'blur(44px) saturate(1.4)',
                      opacity: isPlaying ? 0.5 : 0,
                      transform: 'scale(1.6)',
                      zIndex: 0,
                      maskImage: 'radial-gradient(ellipse at center, black 20%, transparent 70%)',
                      WebkitMaskImage: 'radial-gradient(ellipse at center, black 20%, transparent 70%)',
                    }}
                    aria-hidden="true"
                  >
                    {/* Previous glow - fades out during track change */}
                    {isTransitioning && prevTrackInfo?.artworkUrl && (
                      <img
                        src={prevTrackInfo.artworkUrl}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover rounded-xl animate-track-fade-out"
                      />
                    )}
                    {isTransitioning && prevTrackInfo && !prevTrackInfo.artworkUrl && (
                      <div
                        className={`absolute inset-0 rounded-xl ${getPhaseColor(prevTrackInfo.phase)} animate-track-fade-out`}
                      />
                    )}
                    {/* Current glow - phase color base + artwork overlay */}
                    {currentTrack && (
                      <div
                        key={`phase-${currentTrackIndex}`}
                        className={`absolute inset-0 rounded-xl ${getPhaseColor(currentTrack.phase)} animate-track-fade-in`}
                      />
                    )}
                    {artworkUrl && (
                      <img
                        key={`glow-${currentTrackIndex}-${artworkUrl}`}
                        src={artworkUrl}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover rounded-xl animate-track-fade-in"
                      />
                    )}
                  </div>
                )}
                <div className="relative w-32 h-32 rounded-xl overflow-hidden shadow-lg z-10">
                  {/* Previous artwork - fades out */}
                  {isTransitioning && prevTrackInfo && (
                    <div className="absolute inset-0 animate-track-fade-out">
                      {prevTrackInfo.artworkUrl ? (
                        <img src={prevTrackInfo.artworkUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className={`w-full h-full ${getPhaseColor(prevTrackInfo.phase)} flex items-center justify-center`}>
                          <svg className="w-12 h-12 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                          </svg>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Current artwork - fades in */}
                  {artworkUrl ? (
                    <img
                      key={currentTrackIndex}
                      src={artworkUrl}
                      alt={`Album art for ${currentTrack.title}`}
                      className={`w-full h-full object-cover ${isTransitioning ? 'animate-track-fade-in' : ''}`}
                    />
                  ) : (
                    <div
                      key={currentTrackIndex}
                      className={`w-full h-full ${getPhaseColor(currentTrack.phase)} flex items-center justify-center ${isTransitioning ? 'animate-track-fade-in' : ''}`}
                    >
                      <svg className="w-12 h-12 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              {/* Phase Indicator with Tooltip */}
              <div
                className="flex items-center justify-center gap-2 mb-4 group relative cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 rounded-md px-2 py-1"
                tabIndex={0}
                aria-describedby="phase-description"
              >
                <div className={`w-2 h-2 rounded-full transition-colors duration-300 ${getPhaseColor(currentTrack.phase)}`} />
                <span className="text-sm text-parchment-400 dark:text-white/60 transition-colors duration-200">{PHASE_LABELS[currentTrack.phase]}</span>

                {/* Tooltip */}
                <div
                  id="phase-description"
                  role="tooltip"
                  className="absolute top-full mt-2 left-1/2 -translate-x-1/2 px-3 py-2 bg-earth-900/95 dark:bg-black/90 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-all duration-200 translate-y-1 group-hover:translate-y-0 group-focus:translate-y-0 pointer-events-none z-20 whitespace-normal text-center max-w-[200px] shadow-lg"
                >
                  {PHASE_DESCRIPTIONS[currentTrack.phase]}
                  {/* Arrow */}
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-earth-900/95 dark:bg-black/90 rotate-45" />
                </div>
              </div>

              {/* Track Info with Crossfade */}
              <div className="text-center mb-6 relative">
                {/* Previous track info - fades out */}
                {isTransitioning && prevTrackInfo && (
                  <div className="absolute inset-0 animate-track-fade-out">
                    <h2 className="text-xl font-bold text-parchment-text dark:text-white mb-1 line-clamp-2">{prevTrackInfo.title}</h2>
                    <p className="text-parchment-500 dark:text-white/60">{prevTrackInfo.artist}</p>
                  </div>
                )}
                {/* Current track info - fades in */}
                <div key={currentTrackIndex} className={isTransitioning ? 'animate-track-fade-in' : ''}>
                  <h2 className="text-xl font-bold text-parchment-text dark:text-white mb-1 line-clamp-2">{currentTrack.title}</h2>
                  <p className="text-parchment-500 dark:text-white/60">{currentTrack.artist}</p>
                </div>
              </div>

              {/* Playback Controls */}
              <div className="flex items-center justify-center gap-6 relative">
                {/* Previous */}
                <button
                  onClick={onSkipPrevious}
                  disabled={currentTrackIndex === 0}
                  className="p-3 text-parchment-500 dark:text-white/80 hover:text-parchment-text dark:hover:text-white disabled:text-parchment-300 dark:disabled:text-white/30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous track"
                >
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
                  </svg>
                </button>

                {/* Play/Pause with Tooltip */}
                <div className="relative">
                  <button
                    onClick={isPlaying ? onPause : onPlay}
                    disabled={isLoading}
                    className={`w-16 h-16 rounded-full bg-gradient-to-r from-primary-500 to-accent-500 flex items-center justify-center text-white shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 active:scale-95 transition-all disabled:opacity-50 ${isPlaying && visualEffects.enhancedAnimations ? 'play-pulse' : ''}`}
                    aria-label={isPlaying ? 'Pause' : 'Play'}
                  >
                    {isLoading ? (
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : isPlaying ? (
                      <svg key="pause" className="w-8 h-8 animate-icon-fade-in" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                      </svg>
                    ) : (
                      <svg key="play" className="w-8 h-8 ml-1 animate-icon-fade-in" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    )}
                  </button>
                  {AFFILIATE_LINKS_ENABLED && displayMode === 'tooltip' && (
                    <AffiliateTooltip hasStartedPlaying={hasStartedPlaying} />
                  )}
                </div>

                {/* Next */}
                <button
                  onClick={onSkipNext}
                  disabled={currentTrackIndex >= resolvedTracks.length - 1}
                  className="p-3 text-parchment-500 dark:text-white/80 hover:text-parchment-text dark:hover:text-white disabled:text-parchment-300 dark:disabled:text-white/30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next track"
                >
                  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
                  </svg>
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-parchment-400 dark:text-white/60">No track playing</p>
            </div>
          )}
        </div>

        {/* Affiliate Inline Card - after Now Playing */}
        {AFFILIATE_LINKS_ENABLED && displayMode === 'inline-card' && (
          <AffiliateInlineCard />
        )}

        {/* Phase Chart with hover interaction */}
        <div className="mb-4">
          <TreatmentCurveChart
            treatmentType={treatmentType}
            durationMinutes={durationMinutes}
            phaseRatios={phaseRatios}
            onHoverTimeChange={setHoveredMinute}
            hoveredTrack={tooltipTrack ? { title: tooltipTrack.title, artist: tooltipTrack.artist } : null}
            externalHoverMinute={hoveredTrackMinute}
            glowEnabled={visualEffects.glowEffects}
          />
        </div>

        {/* Phase-Grouped Track List */}
        <div className="flex-1 overflow-y-auto mb-4 space-y-4">
          {tracksByPhase.map(({ phase, tracks }, phaseIndex) => {
            const phaseTracks = tracks;
            const phaseDuration = phaseTracks.reduce((sum, t) => sum + t.durationSeconds, 0);

            return (
              <div key={phase} className="glass rounded-xl p-4 animate-item-enter" style={{ animationDelay: `${phaseIndex * 75}ms` }}>
                {/* Phase Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${getPhaseColor(phase)} transition-colors duration-300`} />
                    <span className="font-medium text-parchment-text dark:text-white">{PHASE_LABELS[phase]}</span>
                  </div>
                  <span className="text-sm text-parchment-400 dark:text-white/50">
                    {phaseTracks.length} {phaseTracks.length === 1 ? 'track' : 'tracks'} · {formatDuration(phaseDuration)}
                  </span>
                </div>

                {/* Track List */}
                <div className="space-y-1">
                  {phaseTracks.map((track, localIndex) => {
                    const globalIndex = getGlobalTrackIndex(phase, localIndex);
                    const isCurrent = globalIndex === currentTrackIndex;
                    const isHighlighted = globalIndex === highlightedTrackIndex;

                    return (
                      <button
                        key={`${track.isrc}-${localIndex}`}
                        onClick={() => onSkipToTrack(globalIndex)}
                        onMouseEnter={() => setHoveredTrackIndex(globalIndex)}
                        onMouseLeave={() => setHoveredTrackIndex(null)}
                        onFocus={() => setHoveredTrackIndex(globalIndex)}
                        onBlur={() => setHoveredTrackIndex(null)}
                        className={`
                          w-full flex items-center gap-3 p-3 rounded-lg text-left transition-all duration-200 animate-item-enter
                          ${isCurrent ? 'bg-black/10 dark:bg-white/10' : ''}
                          ${isHighlighted && !isCurrent ? 'ring-2 ring-primary-400/60 bg-primary-400/10' : ''}
                          ${!isCurrent && !isHighlighted ? 'hover:bg-black/5 dark:hover:bg-white/5' : ''}
                        `}
                        style={{ animationDelay: `${globalIndex * 50}ms` }}
                      >
                        {/* Track Number / Playing Indicator */}
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                          {isCurrent && isPlaying ? (
                            <div className="flex items-end gap-0.5 h-4">
                              <div className="w-1 bg-primary-400 rounded-full animate-pulse" style={{ height: '60%', animationDelay: '0ms' }} />
                              <div className="w-1 bg-primary-400 rounded-full animate-pulse" style={{ height: '100%', animationDelay: '150ms' }} />
                              <div className="w-1 bg-primary-400 rounded-full animate-pulse" style={{ height: '40%', animationDelay: '300ms' }} />
                            </div>
                          ) : (
                            <span className={`text-sm ${isCurrent ? 'text-primary-500 dark:text-primary-400 font-medium' : 'text-parchment-300 dark:text-white/40'}`}>
                              {globalIndex + 1}
                            </span>
                          )}
                        </div>

                        {/* Track Info */}
                        <div className="flex-1 min-w-0">
                          <div className={`text-sm truncate ${isCurrent ? 'text-parchment-text dark:text-white font-medium' : 'text-parchment-text dark:text-white/90'}`}>
                            {track.title}
                          </div>
                          <div className="text-xs text-parchment-400 dark:text-white/50 truncate">{track.artist}</div>
                        </div>

                        {/* Duration */}
                        <span className="text-xs text-parchment-300 dark:text-white/40 flex-shrink-0">
                          {formatDuration(track.durationSeconds)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* Primary action: Save or Open Apple Music */}
          {platform !== 'youtube' && (
            hasSaved ? (
              <>
                <Button
                  variant="secondary"
                  size="lg"
                  href={primaryAppleMusicLink}
                  className="w-full"
                  leftIcon={
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
                    </svg>
                  }
                >
                  Open in Apple Music
                </Button>

                <div className="glass rounded-xl p-3 text-xs text-parchment-500 dark:text-white/70">
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-medium text-parchment-text/80 dark:text-white/80">Apple Music link</span>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!primaryAppleMusicLink) return;
                        try {
                          if (navigator.clipboard?.writeText) {
                            await navigator.clipboard.writeText(primaryAppleMusicLink);
                          } else {
                            const textarea = document.createElement('textarea');
                            textarea.value = primaryAppleMusicLink;
                            textarea.setAttribute('readonly', '');
                            textarea.style.position = 'absolute';
                            textarea.style.left = '-9999px';
                            document.body.appendChild(textarea);
                            textarea.select();
                            document.execCommand('copy');
                            document.body.removeChild(textarea);
                          }
                          setCopyStatus('copied');
                        } catch {
                          setCopyStatus('error');
                        } finally {
                          window.setTimeout(() => setCopyStatus('idle'), 2000);
                        }
                      }}
                      className="px-2.5 py-1 rounded-lg text-xs font-medium text-parchment-text/80 dark:text-white/80 bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/20 transition-colors"
                    >
                      {copyStatus === 'copied' ? 'Copied' : copyStatus === 'error' ? 'Copy failed' : 'Copy link'}
                    </button>
                  </div>
                  <div className="mt-2 rounded-lg bg-black/5 dark:bg-white/10 px-2 py-1 font-mono text-[11px] break-all">
                    {primaryAppleMusicLink || 'Link unavailable'}
                  </div>
                  {appleMusicLinks?.web && (
                    <a
                      href={appleMusicLinks.web}
                      className="mt-2 inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-300 hover:text-primary-700 dark:hover:text-primary-200"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open web link
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 3h7m0 0v7m0-7L10 14" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5h9v9H5z" />
                      </svg>
                    </a>
                  )}
                </div>
              </>
            ) : isPreviewMode && onConnectFromPreview ? (
              <Button
                variant="secondary"
                size="lg"
                onClick={onConnectFromPreview || onSaveToLibrary}
                className="w-full"
                leftIcon={
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
                  </svg>
                }
              >
                Connect to Save
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="lg"
                onClick={onSaveToLibrary}
                disabled={isSaving}
                className="w-full"
                leftIcon={
                  isSaving ? (
                    <div className="w-5 h-5 border-2 border-parchment-400/30 dark:border-white/30 border-t-parchment-500 dark:border-t-white rounded-full animate-spin" />
                  ) : (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
                    </svg>
                  )
                }
              >
                {isSaving ? 'Saving...' : 'Save to Apple Music'}
              </Button>
            )
          )}

          {/* Regenerate Button */}
          <Button
            variant="ghost"
            size="lg"
            onClick={onRegenerate}
            disabled={isSaving}
            className="w-full"
            leftIcon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            }
          >
            Regenerate
          </Button>

          {/* New Playlist Button */}
          <Button
            variant="ghost"
            size="lg"
            onClick={onNewPlaylist}
            disabled={isSaving}
            className="w-full"
            leftIcon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            }
          >
            New Playlist
          </Button>
        </div>

        {/* Setup Instructions */}
        {platform !== 'youtube' && (
          <div className="mt-4">
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="w-full flex items-center justify-center gap-2 py-2 text-sm text-parchment-400 dark:text-white/50 hover:text-parchment-text/80 dark:hover:text-white/70 transition-colors"
          >
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${showInstructions ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            {showInstructions ? 'Hide Setup Instructions' : 'View Setup Instructions'}
          </button>

          {showInstructions && (
            <div className="glass rounded-xl p-5 mt-3 text-sm animate-slide-down">
              <h3 className="font-bold text-parchment-text dark:text-white mb-4 animate-item-enter" style={{ animationDelay: '0ms' }}>Getting Ready for Your Session</h3>

              <div className="space-y-4">
                <div className="animate-item-enter" style={{ animationDelay: '50ms' }}>
                  <h4 className="font-medium text-primary-600 dark:text-primary-300 mb-1">1. Find Your Playlist</h4>
                  <p className="text-parchment-500 dark:text-white/70">
                    In Apple Music, go to <strong>Library → Playlists</strong> and find "<strong>{playlist.name}</strong>"
                  </p>
                </div>

                <div className="animate-item-enter" style={{ animationDelay: '100ms' }}>
                  <h4 className="font-medium text-primary-600 dark:text-primary-300 mb-1">2. Download for Offline</h4>
                  <p className="text-parchment-500 dark:text-white/70">
                    Tap the ••• menu on the playlist and select "<strong>Download</strong>" to ensure uninterrupted playback during your session.
                  </p>
                </div>

                <div className="animate-item-enter" style={{ animationDelay: '150ms' }}>
                  <h4 className="font-medium text-primary-600 dark:text-primary-300 mb-1">3. Enable Crossfade</h4>
                  <p className="text-parchment-500 dark:text-white/70">
                    For seamless transitions, go to <strong>Settings → Music → Crossfade Songs</strong> and set it to <strong>6-12 seconds</strong>.
                  </p>
                </div>

                <div className="pt-3 border-t border-black/10 dark:border-white/10 animate-item-enter" style={{ animationDelay: '200ms' }}>
                  <p className="text-parchment-400 dark:text-white/50 italic">
                    Remember to start the playlist at the beginning of your treatment for optimal phase alignment.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {/* Accessories Section - collapsed mode only */}
        {displayMode === 'collapsed' && <AccessoriesSection />}
      </div>

      {/* Affiliate Sticky Banner - at root level for fixed positioning */}
      {AFFILIATE_LINKS_ENABLED && displayMode === 'sticky-banner' && (
        <AffiliateStickyBanner />
      )}
    </div>
  );
}
